import React, { useEffect, useState } from 'react'
import { adminService, type AuthSettings } from '@/services/adminService'
import { PageHeader } from '@/components/layout'

interface AdminSystemSettingsPageProps {
  service?: typeof adminService
}

export const AdminSystemSettingsPage: React.FC<AdminSystemSettingsPageProps> = ({
  service = adminService,
}) => {
  const [settings, setSettings] = useState<AuthSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    let mounted = true
    service
      .getAuthSettings()
      .then((res) => {
        if (!mounted) return
        if (res.success && res.data) setSettings(res.data)
      })
      .catch((err) => setToast({ type: 'error', text: (err as Error).message }))
      .finally(() => mounted && setLoading(false))
    return () => {
      mounted = false
    }
  }, [service])

  const handleNumberChange = (key: keyof AuthSettings, raw: string) => {
    const v = parseInt(raw, 10)
    if (!settings) return
    setSettings({ ...settings, [key]: Number.isNaN(v) ? 0 : v })
  }

  const handleSave = async () => {
    if (!settings) return
    setSaving(true)
    try {
      const res = await service.updateAuthSettings({
        otpExpiryMinutes: settings.otpExpiryMinutes,
        otpMaxRetries: settings.otpMaxRetries,
        otpResendCooldownSeconds: settings.otpResendCooldownSeconds,
      })
      if (!res.success) throw new Error(res.error?.message || 'Gagal menyimpan')
      setToast({ type: 'success', text: 'Pengaturan tersimpan' })
    } catch (err) {
      setToast({ type: 'error', text: (err as Error).message || 'Gagal menyimpan' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div data-testid="admin-system-settings-page">
      <PageHeader
        title="Pengaturan Sistem"
        subtitle="Konfigurasi parameter keamanan dan autentikasi platform"
      />

      {toast && (
        <div
          role={toast.type === 'error' ? 'alert' : 'status'}
          className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            toast.type === 'success'
              ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
              : 'bg-red-500/10 text-red-500 border border-red-500/20'
          }`}
        >
          <span className="material-symbols-outlined text-[20px]">
            {toast.type === 'success' ? 'check_circle' : 'error'}
          </span>
          <span className="font-medium">{toast.text}</span>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}

      {settings && (
        <div className="max-w-2xl bg-white dark:bg-surface-card rounded-lg border border-slate-200 dark:border-white/5 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-white/[0.02]">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">security</span>
              Autentikasi & OTP
            </h2>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                  Panjang OTP
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={settings.otpLength}
                    readOnly
                    className="w-full px-4 py-2.5 bg-slate-100 dark:bg-slate-900/50 border border-slate-200 dark:border-white/10 rounded-lg text-slate-500 dark:text-slate-500 font-mono cursor-not-allowed"
                    data-testid="otp-length"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[18px]">lock</span>
                </div>
                <p className="mt-1.5 text-[11px] text-slate-400 italic">* Nilai sistem, tidak dapat diubah</p>
              </div>

              <div>
                <label htmlFor="otp-expiry" className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                  OTP Expiry (Menit)
                </label>
                <input
                  id="otp-expiry"
                  type="number"
                  min={1}
                  max={60}
                  value={settings.otpExpiryMinutes}
                  onChange={(e) => handleNumberChange('otpExpiryMinutes', e.target.value)}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white font-mono focus:outline-none focus:border-primary/50 transition-all"
                  data-testid="otp-expiry-minutes"
                />
              </div>

              <div>
                <label htmlFor="otp-retries" className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                  Batas Percobaan (Max Retries)
                </label>
                <input
                  id="otp-retries"
                  type="number"
                  min={1}
                  max={10}
                  value={settings.otpMaxRetries}
                  onChange={(e) => handleNumberChange('otpMaxRetries', e.target.value)}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white font-mono focus:outline-none focus:border-primary/50 transition-all"
                  data-testid="otp-max-retries"
                />
              </div>

              <div>
                <label htmlFor="otp-cooldown" className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                  Cooldown Kirim Ulang (Detik)
                </label>
                <input
                  id="otp-cooldown"
                  type="number"
                  min={0}
                  max={3600}
                  value={settings.otpResendCooldownSeconds}
                  onChange={(e) => handleNumberChange('otpResendCooldownSeconds', e.target.value)}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white font-mono focus:outline-none focus:border-primary/50 transition-all"
                  data-testid="otp-cooldown-seconds"
                />
              </div>
            </div>

            <div className="pt-6 border-t border-slate-100 dark:border-white/5 flex justify-end">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-8 py-2.5 bg-primary text-white rounded-lg font-bold text-sm shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
                data-testid="settings-save"
              >
                <span className="material-symbols-outlined text-[20px]">save</span>
                {saving ? 'Menyimpan...' : 'Simpan Pengaturan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminSystemSettingsPage
